import { useUserStore } from '../Stores/User'
import { useAppState } from '../Stores/AppState';
import router from '../router';
import axios from 'axios';

export class RouteHelper {
    // Flag to indicate entity is already active and we're just syncing URL
    // When true, guard will skip EntityNavigator to prevent LocationAnimation
    static skipEntityNavigation = false;
    /**
     * Get entity URL from entity object with sequence_number
     * @param {Object} entity - Entity object with sequence_number
     * @returns {string|null} - URL in format /work/HANDLE-SEQUENCE or null
     */
    static getEntityUrl(entity) {
        if (!entity?.sequence_number) {
            console.warn('RouteHelper: Entity missing sequence_number', entity);
            return null;
        }

        const userStore = useUserStore();
        const handle = userStore.profile?.organization?.handle;

        if (!handle) {
            console.warn('RouteHelper: Unable to get organization handle');
            return null;
        }

        return `/work/${handle}-${entity.sequence_number}`;
    }

    /**
     * Navigate to entity with full LocationAnimation (for breadcrumbs, address bar, shared links)
     * This will trigger the route guard which handles entity loading and animation
     * @param {Object} entity - Entity object with sequence_number
     */
    static navigateToEntity(entity) {
        const url = this.getEntityUrl(entity);

        if (!url) {
            console.warn('RouteHelper: Unable to construct entity URL', entity);
            return;
        }

        // Check if we're already on this URL to prevent unnecessary navigation
        if (router.currentRoute.value.path === url) {
            return;
        }

        // Normal navigation - route guard will handle LocationAnimation
        router.push(url);
    }

    /**
     * Update URL ONLY for already-active entity (ONLY for work panel clicks)
     * Called from click handlers in WorkPanel components (Space/Board/Item.vue)
     * Sets flag to prevent LocationAnimation when entity is already active
     * @param {Object} entity - Entity object with sequence_number
     */
    static updateUrlForEntity(entity) {
        const url = this.getEntityUrl(entity);

        if (!url) {
            console.warn('RouteHelper: Unable to construct entity URL', entity);
            return;
        }

        // Check if we're already on this URL to prevent unnecessary router.push
        if (router.currentRoute.value.path === url) {
            return;
        }

        // Set flag before navigation - indicates entity is already active
        // Guard will check this and skip EntityNavigator/LocationAnimation
        this.stopEntityNavigation()

        // Update URL - guard will see flag and skip navigation logic
        // Note: Flag will be reset by the guard after it checks it
        router.push(url);
    }

    static stopEntityNavigation()
    {
        this.skipEntityNavigation = true;
    }

    /**
     * Get entity URL from entity ID by calling resolve API
     * @param {string} entityType - Entity type (Item, Board, Workspace, etc.)
     * @param {number} entityId - Entity ID
     * @returns {Promise<string|null>} - URL in format /work/HANDLE-SEQUENCE or null
     */
    static async getEntityUrlFromId(entityType, entityId) {
        try {
            const response = await axios.get(`/entity-id-to-universal/${entityId}`, {
                params: { type: entityType }
            });

            if (response.data.success) {
                return {
                    url: `/work/${response.data.data.universal_id}`,
                    universal_id: response.data.data.universal_id
                };
            }

            console.error('Failed to get universal ID:', response.data.message);
            return null;
        } catch (error) {
            console.error('Error getting entity URL from ID:', error);
            return null;
        }
    }

    /**
     * Navigate to entity by ID with full LocationAnimation (for breadcrumbs, activity panel, etc.)
     * Calls API to get universal ID first, then navigates via router
     * Route guard will handle entity loading and animation
     * @param {string} entityType - Entity type
     * @param {number} entityId - Entity ID
     */
    static async navigateToEntityById(entityType, entityId) {
        const data = await this.getEntityUrlFromId(entityType, entityId);

        if (router.currentRoute.value.path === data?.url) {
            // Direct navigation - instead of router.push, we call the function to handle the entity link already active.
            await useAppState().handleEntityLinkAlreadyActiveByUniversalId(data.universal_id);
        }
        else if (data?.url) {
            // Normal navigation - route guard will handle LocationAnimation
            router.push(data.url);
        } else {
            console.error('Unable to navigate to entity by ID');
        }
    }
}
